import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.#controller.signal.aborted) this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('pointerover', this.#handleThumbnailOver, { signal });
    this.addEventListener('pointerout', this.#handleThumbnailOut, { signal });
    this.addEventListener('pointerdown', this.#cancelThumbnailHover, { signal });
    this.addEventListener('keydown', this.#cancelThumbnailHover, { signal });
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#cancelThumbnailHover();
    this.#controller.abort();
  }

  #thumbnailHoverTimer;

  #cancelThumbnailHover = () => {
    clearTimeout(this.#thumbnailHoverTimer);
    this.#thumbnailHoverTimer = undefined;
  };

  #getThumbnail(target) {
    if (!(target instanceof Element)) return;
    const thumbnail = target.closest('button.slideshow-controls__thumbnail[ref="thumbnails[]"]');
    if (!thumbnail || thumbnail.closest('dialog') || thumbnail.closest('slideshow-component') !== this.slideshow) return;
    return thumbnail;
  }

  #canHoverThumbnail(thumbnail) {
    return (
      this.isConnected &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches &&
      !thumbnail.disabled &&
      !thumbnail.closest('[hidden], [inert], [aria-hidden="true"]') &&
      thumbnail.getClientRects().length > 0 &&
      getComputedStyle(thumbnail).visibility === 'visible'
    );
  }

  #handleThumbnailOver = (event) => {
    const thumbnail = this.#getThumbnail(event.target);
    if (!thumbnail || (event.relatedTarget instanceof Node && thumbnail.contains(event.relatedTarget))) return;
    this.#cancelThumbnailHover();
    if (event.pointerType !== 'mouse' || !this.#canHoverThumbnail(thumbnail)) return;
    const selection = thumbnail.getAttribute('on:click')?.match(/^\/select\/(\d+)$/);
    if (!selection) return;

    this.#thumbnailHoverTimer = setTimeout(() => {
      this.#thumbnailHoverTimer = undefined;
      if (!this.#canHoverThumbnail(thumbnail) || !thumbnail.matches(':hover')) return;
      if (this.#getThumbnail(thumbnail) !== thumbnail || thumbnail.getAttribute('aria-selected') === 'true') return;
      this.slideshow?.select(Number(selection[1]), event, { animate: false });
    }, 200);
  };

  #handleThumbnailOut = (event) => {
    const thumbnail = this.#getThumbnail(event.target);
    if (!thumbnail || (event.relatedTarget instanceof Node && thumbnail.contains(event.relatedTarget))) return;
    this.#cancelThumbnailHover();
  };

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
